/**
 * Jettison (M8 TR-8b2; M6 spec §6.4.2, §6.4.5; D-523, D-524): the pilot drops a share of the hold to end an
 * interdiction. Pure and immutable: `jettisonShare` takes a Career and returns the new Career plus the pod's
 * `CargoContents` (the death pod's shape), or a refusal. The bridge hook (`bridge/m2/encounters.ts attachJettison`)
 * commits the change through the career service's `applyDevCommand` and spawns the pod.
 *
 * - **What drops:** `share` of every ware and every good (`cargo.parts`). Components never drop: salvaged parts,
 *   loose pods, data cores, sealed, story and contract cargo, and contraband (a component flag).
 * - **Tetherstone stays (D-524):** it is a ware, but dumping it can't dodge a patrol scan, so it never drops.
 * - **Refusals:** `nothing` (no ware other than tetherstone and no good aboard) and `tooSmall` (the drop's base value
 *   is under `minDropValue` and the share is not everything). Share 1 is always accepted when anything can drop.
 * - **No credits:** the wallet, the debt and the lease are never touched.
 */
import { z } from 'zod'
import rawConfig from '../data/jettison.json'
import { WARE_IDS, type M2Data, type WareId } from '../data/m2Schemas'
import { contentsValue } from './cargo'
import { EPSILON, emptyWares, withGood, withWare } from './ops'
import { GOOD_IDS, type Goods } from './parts'
import type { CargoContents, Career } from './types'

const text = z.string().min(1)

export const jettisonConfigSchema = z.strictObject({
  comment: z.string().optional(),
  podClaimS: z.number().nonnegative(),
  shares: z
    .array(z.number().gt(0).max(1))
    .min(1)
    .refine((s) => s.every((v, i) => i === 0 || v > s[i - 1]), 'shares: must rise'),
  minDropValue: z.number().nonnegative(),
  podOffsetM: z.number().nonnegative(),
  keys: z.array(z.string().min(1)).min(1),
  padButton: z.number().int().nonnegative(),
  holdS: z.number().positive(),
  noticeS: z.number().positive(),
  text: z.strictObject({
    offer: text,
    offerHint: text,
    all: text,
    dropped: text,
    podTag: text,
    podTagOpen: text,
    recovered: text,
    nothing: text,
    tooSmall: text,
    failed: text,
  }),
})
export type JettisonConfig = z.infer<typeof jettisonConfigSchema>

export function loadJettisonConfig(raw: unknown = rawConfig): JettisonConfig {
  return jettisonConfigSchema.parse(raw)
}

/** Wares that never drop (D-524: illegal tetherstone keeps its scan risk). */
export const JETTISON_KEPT_WARES: readonly WareId[] = Object.freeze(['tetherstone'])

export type JettisonRefusal = 'nothing' | 'tooSmall'
export type JettisonResult =
  | { readonly ok: true; readonly career: Career; readonly contents: CargoContents; readonly value: number }
  | { readonly ok: false; readonly reason: JettisonRefusal }

/** What `jettisonShare` reads: the ware base prices and the minimum drop. */
export interface JettisonData {
  readonly m2: Pick<M2Data, 'wares'>
  readonly config: Pick<JettisonConfig, 'minDropValue'>
}

/**
 * Drops `share` (0 < share ≤ 1) of every ware (but tetherstone) and every good into one pod's contents. Throws a
 * RangeError on a share outside (0, 1] (a bug: the HUD offers only `jettison.json shares`).
 */
export function jettisonShare(career: Career, share: number, data: JettisonData): JettisonResult {
  if (!(share > 0 && share <= 1)) throw new RangeError(`jettisonShare: share ${share} is outside (0, 1]`)
  const all = share === 1
  const wares = emptyWares()
  let next = career
  let any = false
  for (const w of WARE_IDS) {
    if (JETTISON_KEPT_WARES.includes(w)) continue
    const have = career.cargo.wares[w]
    if (!(have > EPSILON)) continue
    const drop = all ? have : have * share
    wares[w] = drop
    next = withWare(next, w, -drop)
    any = true
  }
  const held = career.cargo.parts
  let goods: Goods | undefined
  if (held !== undefined) {
    for (const g of GOOD_IDS) {
      const have = held[g] ?? 0
      if (!(have > EPSILON)) continue
      const drop = all ? have : have * share
      goods = { ...goods, [g]: drop }
      next = withGood(next, g, -drop)
      any = true
    }
  }
  if (!any) return { ok: false, reason: 'nothing' }
  const contents: CargoContents = goods === undefined ? { wares, components: [] } : { wares, goods, components: [] }
  const value = contentsValue(contents, data.m2)
  if (!all && value < data.config.minDropValue) return { ok: false, reason: 'tooSmall' }
  return { ok: true, career: next, contents, value }
}

/** Puts a pod's wares and goods back into the hold (the bridge's undo when no pod could spawn). Pure. */
export function restoreJettisoned(career: Career, contents: CargoContents): Career {
  let next = career
  for (const w of WARE_IDS) {
    const t = contents.wares[w]
    if (t > 0) next = withWare(next, w, t)
  }
  const goods = contents.goods
  if (goods !== undefined) {
    for (const g of GOOD_IDS) {
      const q = goods[g] ?? 0
      if (q > 0) next = withGood(next, g, q)
    }
  }
  return next
}
